import * as React from "react";
import {
  InProcessSettingsDto,
  defaultInProcessSettings,
  useInProcessSettingsQuery,
  useSaveInProcessSettings,
} from "../api/queries/useInProcessSettings";

type InProcessSettingsContextValue = {
  settings: InProcessSettingsDto;
  setSettings: (next: InProcessSettingsDto) => void;
  isLoading: boolean;
  saveSettings: (next: InProcessSettingsDto) => Promise<void>;
};

const InProcessSettingsContext = React.createContext<
  InProcessSettingsContextValue | undefined
>(undefined);

type ProviderProps = {
  children: React.ReactNode;
};

function InProcessSettingsProvider({ children }: ProviderProps) {
  const { data, isLoading } = useInProcessSettingsQuery();
  const { mutateAsync: saveSettingsMutation } = useSaveInProcessSettings();

  const [settings, setSettings] = React.useState<InProcessSettingsDto>(
    data || defaultInProcessSettings
  );

  React.useEffect(() => {
    if (data) {
      setSettings(data);
    }
  }, [data]);

  const saveSettings = React.useCallback(
    async (next: InProcessSettingsDto) => {
      setSettings(next);
      await saveSettingsMutation(next);
    },
    [saveSettingsMutation]
  );

  const value = React.useMemo(
    () => ({
      settings,
      setSettings,
      saveSettings,
      isLoading,
    }),
    [settings, isLoading, saveSettings]
  );

  return (
    <InProcessSettingsContext.Provider value={value}>
      {children}
    </InProcessSettingsContext.Provider>
  );
}

function useInProcessSettings() {
  const context = React.useContext(InProcessSettingsContext);
  if (!context) {
    throw new Error(
      "useInProcessSettings must be used within InProcessSettingsProvider"
    );
  }
  return context;
}

export { InProcessSettingsProvider, useInProcessSettings };
